import { HStack, PageContainer, PageHeader, Spread, VStack } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { AppliedFiltersBar } from '@/components/DataTable/AppliedFiltersBar'
import { Filter, FilterControls } from '@/components/DataTable/FilterControls'
import config from '@/config'
import { useCrtCurrencySync } from '@/hooks/crt/useCrtCurrencySync'
import { useCrtPeriodSync } from '@/hooks/crt/useCrtPeriodSync'
import useBrandFilter from '@/hooks/filters/useBrandFilter'
import useMarkingFilter from '@/hooks/filters/useMarkingFilter'
import useMaterialDistributionChannelFilter from '@/hooks/filters/useMaterialDistributionChannelFilter'
import useMaterialFilter from '@/hooks/filters/useMaterialFilter'
import useMaterialGroupFilter from '@/hooks/filters/useMaterialGroupFilter'
import useProductCategoryFilter from '@/hooks/filters/useProductCategoryFilter'
import useProductTypeFilter from '@/hooks/filters/useProductTypeFilter'
import useVariantFilter from '@/hooks/filters/useVariantFilter'
import { pnlTableSearchSchema, useActualsPnl, useForecastsPnl } from '@/hooks/queries/crt/usePnl'
import { getClearedFilters, resetFiltersPanel, useAppliedFilters } from '@/lib/utils/filters'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CostOfSalesCard } from './-components/cost-of-sales/CostOfSalesCard'
import { KpiOverviewCard } from './-components/overview/KpiOverviewCard'
import { RetailPriceCard } from './-components/retail-price/RetailPriceCard'
import { TaxesCard } from './-components/taxes/TaxesCard'
import { TradeMarginsCard } from './-components/trade-margins/TradeMarginsCard'

export const Route = createFileRoute('/_auth/_modules/crt/kpi-reports/graphs')({
  // The graphs view is gated behind a feature flag; fall back to the P&L Summary when it is off.
  beforeLoad: () => {
    if (!config.featureFlags.viewPnlGraphs) {
      throw redirect({ to: '/crt/kpi-reports/profits-and-loss-summary' })
    }
  },
  component: RouteComponent,
  validateSearch: (search) => pnlTableSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { crtPeriod: currentCrtPeriod } = useCrtPeriodSync({
    search,
    navigate: (search) => navigate({ search }),
  })

  // Currency is display-only and picked in the header: it never reaches the backend, so it stays out of
  // `appliedFilters`. The tiles read it from the atom; this only mirrors it into the URL, after the period
  // sync so both can land on arrival.
  useCrtCurrencySync({
    search,
    navigate: (update) => navigate({ search: update, replace: true }),
  })

  const { productCategoryFilter, productCategories, setProductCategories } =
    useProductCategoryFilter(search.productCategory)
  const { brandFilter, brands, setBrands } = useBrandFilter(search.brand)
  const { variantFilter, variants, setVariants } = useVariantFilter(search.variant)
  const { materialGroupFilter, materialGroups, setMaterialGroups } = useMaterialGroupFilter(
    search.materialGroupCode,
  )
  const { markingFilter, marking, setMarking } = useMarkingFilter(search.marking)
  const { productTypeFilter, productTypes, setProductTypes } = useProductTypeFilter(
    search.productType,
  )
  const { materialFilter, material, setMaterial } = useMaterialFilter(search.material)
  const { distributionChannelFilter, distributionChannels, setDistributionChannels } =
    useMaterialDistributionChannelFilter(search.distributionChannel)

  const filters = [
    productCategoryFilter,
    brandFilter,
    variantFilter,
    materialGroupFilter,
    markingFilter,
    productTypeFilter,
    materialFilter,
    distributionChannelFilter,
  ]

  const selectedFilters = {
    productCategory: productCategories?.length ? productCategories : undefined,
    brand: brands?.length ? brands : undefined,
    variant: variants?.length ? variants : undefined,
    materialGroupCode: materialGroups?.length ? materialGroups : undefined,
    marking: marking?.length ? marking : undefined,
    productType: productTypes?.length ? productTypes : undefined,
    material: material || undefined,
    distributionChannel: distributionChannels?.length ? distributionChannels : undefined,
  }

  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false)

  const appliedFilterBadges = useAppliedFilters(filters as Filter<unknown>[], search)

  // Re-sync the panel's local state to the applied URL params whenever it opens, so an aborted edit
  // (open → change → close without applying) does not linger on the next open.
  const handleFiltersPanelOpenChange = () => {
    setIsFiltersPanelOpen((open) => {
      if (!open) resetFiltersPanel(filters as Filter<unknown>[], search)
      return !open
    })
  }

  const showResults = () => {
    navigate({ search: { ...search, ...selectedFilters } })
  }

  const resetAllFilters = () => {
    setProductCategories([])
    setBrands([])
    setVariants([])
    setMaterialGroups([])
    setMarking([])
    setProductTypes([])
    setMaterial('')
    setDistributionChannels([])
    navigate({ search: { ...search, ...getClearedFilters(selectedFilters) } })
  }

  const appliedFilters = {
    productCategory: search.productCategory,
    brand: search.brand,
    variant: search.variant,
    materialGroupCode: search.materialGroupCode,
    marking: search.marking,
    productType: search.productType,
    material: search.material,
    distributionChannel: search.distributionChannel,
  }

  const {
    data: actualsData,
    isPending: isActualsPending,
    error: actualsError,
  } = useActualsPnl(currentCrtPeriod, appliedFilters)
  const {
    data: forecastsData,
    isPending: isForecastsPending,
    error: forecastsError,
  } = useForecastsPnl(currentCrtPeriod, appliedFilters)

  const isPending = isActualsPending || isForecastsPending
  // Either half failing leaves the other reading as if the missing one were genuinely zero, so both have
  // to reach the error state. Nothing catches a failed query globally to fall back on.
  const error = actualsError ?? forecastsError
  const months = actualsData?.meta.months ?? []
  const crtPeriod = actualsData?.meta.crtPeriod ?? ''

  return (
    <PageContainer>
      <Spread>
        <PageHeader>{t('kpiReports.profitsAndLossSummary.pageHeader')}</PageHeader>
        <HStack gap="sm">
          <FilterControls
            filters={filters as Filter<unknown>[]}
            activeFilterCount={appliedFilterBadges.length}
            isOpen={isFiltersPanelOpen}
            onOpenChange={handleFiltersPanelOpenChange}
            onShowResults={showResults}
            onResetAllFilters={resetAllFilters}
          />
        </HStack>
      </Spread>

      {/* Several cards below, none owning the filters, so the bar gets its own white card. */}
      {appliedFilterBadges.length > 0 && (
        <Card className="w-full">
          <CardContent className="pt-6">
            <AppliedFiltersBar
              appliedFilters={appliedFilterBadges}
              onBadgeClick={() => setIsFiltersPanelOpen(true)}
            />
          </CardContent>
        </Card>
      )}

      <VStack gap="lg">
        <KpiOverviewCard
          actuals={actualsData?.breakdown}
          forecast={forecastsData?.breakdown}
          months={months}
          crtPeriod={crtPeriod}
          isPending={isPending}
          error={error}
        />
        <TradeMarginsCard
          actuals={actualsData?.breakdown?.tradeMargins}
          forecast={forecastsData?.breakdown.tradeMargins}
          months={months}
          crtPeriod={crtPeriod}
          isPending={isPending}
          error={error}
        />
        <CostOfSalesCard
          actuals={actualsData?.breakdown?.costOfSales}
          forecast={forecastsData?.breakdown.costOfSales}
          months={months}
          crtPeriod={crtPeriod}
          isPending={isPending}
          error={error}
        />
        <TaxesCard
          actuals={actualsData?.breakdown}
          forecast={forecastsData?.breakdown}
          months={months}
          crtPeriod={crtPeriod}
          isPending={isPending}
          error={error}
        />
        <RetailPriceCard
          actuals={actualsData?.breakdown}
          forecast={forecastsData?.breakdown}
          months={months}
          crtPeriod={crtPeriod}
          isPending={isPending}
          error={error}
        />
      </VStack>
    </PageContainer>
  )
}
